import React, { memo, useState, useCallback, useMemo } from 'react'
import { Icon } from '../../../atoms/Icon'
import { Button } from '../../../atoms/Button'
import { SearchInput } from '../../../atoms'
import { Loading } from '../../../atoms/Loading'
import { DealCard } from '../DealCard'
import {
  ListContainer,
  ListHeader,
  ListActions,
  ListContent,
  ListView,
  GridView,
  EmptyState,
  ListFooter,
  PaginationContainer,
  BulkActionsBar,
  FilterChips,
} from './DealList.styles'
import { DealListProps } from './DealList.types'
import { filterDeals, sortDeals } from '../../utils/deal.utils'

/**
 * DealList Component
 *
 * A flexible list component for displaying and managing multiple deals.
 * Supports list and grid views, filtering, sorting, pagination, and bulk actions.
 *
 * @example
 * ```tsx
 * <DealList
 *   deals={dealsData}
 *   view="grid"
 *   onDealSelect={handleDealSelect}
 *   showFilters
 *   showBulkActions
 *   pageSize={20}
 * />
 * ```
 */
export const DealList = memo<DealListProps>(
  ({
    deals = [],
    loading = false,
    error = null,
    view = 'list',
    selectable = false,
    selectedIds = new Set(),
    filters = {},
    sort = [],
    pagination,
    showSearch = true,
    showViewToggle = true,
    showFilters = false,
    showBulkActions = false,
    showPagination = true,
    cardVariant = 'default',
    cardSize = 'medium',
    columns = 3,
    emptyStateTitle = 'No deals found',
    emptyStateDescription = 'Try adjusting your filters or create a new deal',
    customActions,
    bulkActions,
    onDealSelect,
    onDealEdit,
    onDealDelete,
    onSelectionChange,
    onFiltersChange,
    onSortChange,
    onPageChange,
    onViewChange,
    onCreateDeal,
    className,
    style,
  }) => {
    const [localSearch, setLocalSearch] = useState('')
    const [localView, setLocalView] = useState(view)

    // Apply filters and sorting
    const processedDeals = useMemo(() => {
      let result = [...deals]

      // Apply search filter
      if (localSearch) {
        result = filterDeals(result, { ...filters, search: localSearch })
      } else if (filters) {
        result = filterDeals(result, filters)
      }

      // Apply sorting
      if (sort && sort.length > 0) {
        result = sortDeals(result, sort)
      }

      return result
    }, [deals, filters, sort, localSearch])

    // Paginated deals
    const paginatedDeals = useMemo(() => {
      if (!pagination) return processedDeals

      const { page = 1, pageSize = 20 } = pagination
      const start = (page - 1) * pageSize
      const end = start + pageSize

      return processedDeals.slice(start, end)
    }, [processedDeals, pagination])

    // Event handlers
    const handleSelectAll = useCallback(() => {
      if (onSelectionChange) {
        const allIds = new Set(paginatedDeals.map((d) => d.id))
        onSelectionChange(allIds)
      }
    }, [paginatedDeals, onSelectionChange])

    const handleDeselectAll = useCallback(() => {
      if (onSelectionChange) {
        onSelectionChange(new Set())
      }
    }, [onSelectionChange])

    const handleDealSelect = useCallback(
      (dealId: string) => {
        if (!selectable || !onSelectionChange) {
          if (onDealSelect) {
            const deal = deals.find((d) => d.id === dealId)
            if (deal) onDealSelect(deal)
          }
          return
        }

        const newSelection = new Set(selectedIds)
        if (newSelection.has(dealId)) {
          newSelection.delete(dealId)
        } else {
          newSelection.add(dealId)
        }
        onSelectionChange(newSelection)
      },
      [selectable, selectedIds, onSelectionChange, onDealSelect, deals]
    )

    const handleViewChange = useCallback(
      (newView: 'list' | 'grid') => {
        setLocalView(newView)
        if (onViewChange) {
          onViewChange(newView)
        }
      },
      [onViewChange]
    )

    const handleBulkAction = useCallback(
      (actionId: string) => {
        const action = bulkActions?.find((a) => a.id === actionId)
        if (action) {
          const selectedDeals = deals.filter((d) => selectedIds.has(d.id))
          action.action(selectedDeals)
        }
      },
      [bulkActions, deals, selectedIds]
    )

    // Render loading state
    if (loading) {
      return (
        <ListContainer className={className} style={style}>
          <Loading size="large" />
        </ListContainer>
      )
    }

    // Render error state
    if (error) {
      return (
        <ListContainer className={className} style={style}>
          <EmptyState>
            <Icon name="error" size="large" color="#ff3b30" />
            <h3>Error loading deals</h3>
            <p>{error}</p>
          </EmptyState>
        </ListContainer>
      )
    }

    // Render empty state
    if (paginatedDeals.length === 0) {
      return (
        <ListContainer className={className} style={style}>
          <ListHeader>
            {showSearch && (
              <SearchInput
                value={localSearch}
                onChange={setLocalSearch}
                placeholder="Search deals..."
                clearable
              />
            )}
            {onCreateDeal && (
              <Button variant="primary" size="medium" onClick={onCreateDeal}>
                <Icon name="plus" size="small" />
                New Deal
              </Button>
            )}
          </ListHeader>
          <EmptyState>
            <Icon name="deal" size="large" color="#8e8e93" />
            <h3>{emptyStateTitle}</h3>
            <p>{emptyStateDescription}</p>
            {onCreateDeal && (
              <Button variant="primary" size="medium" onClick={onCreateDeal}>
                Create Deal
              </Button>
            )}
          </EmptyState>
        </ListContainer>
      )
    }

    const totalPages = pagination
      ? Math.ceil(processedDeals.length / (pagination.pageSize || 20))
      : 1

    return (
      <ListContainer className={className} style={style}>
        <ListHeader>
          <ListActions>
            {showSearch && (
              <SearchInput
                value={localSearch}
                onChange={setLocalSearch}
                placeholder="Search deals..."
                clearable
              />
            )}

            {showViewToggle && (
              <div>
                <Button
                  variant={localView === 'list' ? 'primary' : 'ghost'}
                  size="small"
                  onClick={() => handleViewChange('list')}
                >
                  <Icon name="list" size="small" />
                </Button>
                <Button
                  variant={localView === 'grid' ? 'primary' : 'ghost'}
                  size="small"
                  onClick={() => handleViewChange('grid')}
                >
                  <Icon name="grid" size="small" />
                </Button>
              </div>
            )}

            {customActions?.map((action) => (
              <Button
                key={action.id}
                variant="secondary"
                size="small"
                onClick={() => action.action(paginatedDeals)}
              >
                {action.icon && <Icon name={action.icon} size="small" />}
                {action.label}
              </Button>
            ))}

            {onCreateDeal && (
              <Button variant="primary" size="medium" onClick={onCreateDeal}>
                <Icon name="plus" size="small" />
                New Deal
              </Button>
            )}
          </ListActions>

          {selectable && (
            <div>
              <Button
                variant="ghost"
                size="small"
                onClick={handleSelectAll}
                disabled={selectedIds.size === paginatedDeals.length}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={handleDeselectAll}
                disabled={selectedIds.size === 0}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </ListHeader>

        {showFilters && filters && Object.keys(filters).length > 0 && (
          <FilterChips>
            {Object.entries(filters).map(([key, value]) => (
              <div key={key}>
                {key}: {JSON.stringify(value)}
                <button
                  onClick={() => {
                    const newFilters = { ...filters }
                    delete newFilters[key as keyof typeof filters]
                    onFiltersChange?.(newFilters)
                  }}
                >
                  <Icon name="close" size="small" />
                </button>
              </div>
            ))}
          </FilterChips>
        )}

        <ListContent>
          {localView === 'list' ? (
            <ListView>
              {paginatedDeals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  variant={cardVariant}
                  size={cardSize}
                  selected={selectedIds.has(deal.id)}
                  onSelect={() => handleDealSelect(deal.id)}
                  onEdit={onDealEdit}
                  onDelete={onDealDelete}
                  showActions
                />
              ))}
            </ListView>
          ) : (
            <GridView $columns={columns}>
              {paginatedDeals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  variant={cardVariant}
                  size={cardSize}
                  selected={selectedIds.has(deal.id)}
                  onSelect={() => handleDealSelect(deal.id)}
                  onEdit={onDealEdit}
                  onDelete={onDealDelete}
                  showActions
                />
              ))}
            </GridView>
          )}
        </ListContent>

        {showPagination && pagination && totalPages > 1 && (
          <ListFooter>
            <PaginationContainer>
              <span>
                Showing{' '}
                {((pagination.page || 1) - 1) * (pagination.pageSize || 20) + 1}{' '}
                -{' '}
                {Math.min(
                  (pagination.page || 1) * (pagination.pageSize || 20),
                  processedDeals.length
                )}{' '}
                of {processedDeals.length} deals
              </span>
              <div>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => onPageChange?.((pagination.page || 1) - 1)}
                  disabled={(pagination.page || 1) === 1}
                >
                  Previous
                </Button>
                <span>
                  {pagination.page || 1} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => onPageChange?.((pagination.page || 1) + 1)}
                  disabled={(pagination.page || 1) === totalPages}
                >
                  Next
                </Button>
              </div>
            </PaginationContainer>
          </ListFooter>
        )}

        {showBulkActions && selectedIds.size > 0 && bulkActions && (
          <BulkActionsBar>
            <span>{selectedIds.size} deals selected</span>
            <div>
              {bulkActions.map((action) => (
                <Button
                  key={action.id}
                  variant={action.variant || 'secondary'}
                  size="small"
                  onClick={() => handleBulkAction(action.id)}
                >
                  {action.icon && <Icon name={action.icon} size="small" />}
                  {action.label}
                </Button>
              ))}
            </div>
          </BulkActionsBar>
        )}
      </ListContainer>
    )
  }
)

DealList.displayName = 'DealList'
